import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import toaster from '../../../../Utils/toaster'
import {usePresence} from '../../../context/PresenceContext'
import {InterpreterModal} from '../DashboardOperation/InterpreterModal'
import {
  getOpiDashboard,
  getOpiHistorical,
  getOpiLive,
  getOpiVolumeExpected,
  sendOpiNotify,
  type OpiDashboardData,
  type OpiFilters,
  type OpiHourlySummary,
  type OpiVolumeExpectedCache,
} from './_requests'
import {OpiScorecards} from './OpiScorecards'
import {OpiCallsTable} from './OpiCallsTable'
import {VolumeExpectedPanel} from './VolumeExpectedPanel'
import {OpiFilterBar} from './OpiFilterBar'
import {HourlyCallsChart} from './HourlyCallsChart'
import {CallsSummaryTable} from './CallsSummaryTable'
import {OpiCardSkeleton} from './OpiCardSkeleton'
import './OpiDashboard.scss'

const LIVE_POLL_MS = 30_000

/**
 * Data sources:
 * - Online Terps / Queue / Live → Presence (socket)
 * - Initial load → POST /opi/overview (scorecards, abandoned, hourly)
 * - Volume Expected → POST /opi/volume (cached, server countdown)
 * - 30s soft refresh → POST /opi/live (no volume)
 * - Chart Apply → /opi/historical (stops live hourly overwrite until remount)
 */
export const OpiDashboard: React.FC = () => {
  const {callQueues, liveCalls, isLoading: presenceLoading, presenceSnapshot} = usePresence()
  const queuedCallIds = useMemo(() => new Set(callQueues.map((call) => call.callId)), [callQueues])
  const connectedLiveCalls = useMemo(
    () => liveCalls.filter((call) => !queuedCallIds.has(call.callId)),
    [liveCalls, queuedCallIds]
  )

  const [data, setData] = useState<OpiDashboardData | null>(null)
  const [volume, setVolume] = useState<OpiVolumeExpectedCache | null>(null)
  const [hourlyRows, setHourlyRows] = useState<OpiHourlySummary[]>([])
  const [hourlyTotals, setHourlyTotals] = useState<OpiHourlySummary | null>(null)
  const [apiLoading, setApiLoading] = useState(true)
  const [volumeLoading, setVolumeLoading] = useState(true)
  const [historicalLoading, setHistoricalLoading] = useState(false)
  const [error, setError] = useState(false)
  const [showInterpreterModal, setShowInterpreterModal] = useState(false)
  const [notifyingCode, setNotifyingCode] = useState<string | null>(null)

  const historicalAppliedRef = useRef(false)
  const liveInFlightRef = useRef(false)
  const volumeCacheVersionRef = useRef<string | null>(null)
  const volumeExpiryPollRef = useRef<number | null>(null)

  const applyVolumeSnapshot = useCallback((result: OpiVolumeExpectedCache) => {
    volumeCacheVersionRef.current = result.lastUpdated
    setVolume(result)
    setVolumeLoading(false)
  }, [])

  /**
   * Called when local countdown hits 0.
   * Waits for background service to refresh cache, then applies new snapshot (~59:59 from server).
   */
  const loadVolumeOnExpiry = useCallback(async () => {
    if (volumeExpiryPollRef.current != null) return
    const snapshotAtExpiry = volumeCacheVersionRef.current

    const poll = async () => {
      try {
        const result = await getOpiVolumeExpected()
        const cacheRefreshed =
          result.secondsUntilRefresh > 0 && result.lastUpdated !== snapshotAtExpiry
        if (cacheRefreshed) {
          applyVolumeSnapshot(result)
          return
        }
      } catch {
        // keep polling
      }
      volumeExpiryPollRef.current = window.setTimeout(() => {
        volumeExpiryPollRef.current = null
        poll()
      }, 5000)
    }

    await poll()
  }, [applyVolumeSnapshot])

  /** Initial load only — reads cache + server remaining seconds. */
  const loadVolumeInitial = useCallback(async () => {
    try {
      const result = await getOpiVolumeExpected()
      applyVolumeSnapshot(result)
      if (result.secondsUntilRefresh <= 0) {
        await loadVolumeOnExpiry()
      }
    } catch (e: any) {
      toaster('error', e?.response?.data?.text || 'Failed to load volume expected.')
      setVolumeLoading(false)
    }
  }, [applyVolumeSnapshot, loadVolumeOnExpiry])

  const loadLiveOverview = useCallback(async (opts?: {soft?: boolean}) => {
    const soft = opts?.soft === true
    if (!soft) setApiLoading(true)
    setError(false)
    try {
      const result = await getOpiDashboard({})
      setData(result)
      if (!historicalAppliedRef.current) {
        setHourlyRows(result.hourlySummary)
        setHourlyTotals(result.hourlyTotals)
      }
    } catch (e: any) {
      setError(true)
      toaster('error', e?.response?.data?.text || 'Failed to load OPI dashboard.')
    } finally {
      if (!soft) setApiLoading(false)
    }
  }, [])

  const softLiveRefresh = useCallback(async () => {
    if (liveInFlightRef.current) return
    liveInFlightRef.current = true
    try {
      const live = await getOpiLive({})
      setData((prev) => {
        if (!prev) {
          return {
            scorecards: live.scorecards,
            topAbandonedLanguages: live.topAbandonedLanguages,
            hourlySummary: live.hourlySummary,
            hourlyTotals: live.hourlyTotals,
          }
        }
        return {
          ...prev,
          scorecards: live.scorecards,
          topAbandonedLanguages: live.topAbandonedLanguages,
          hourlySummary: historicalAppliedRef.current ? prev.hourlySummary : live.hourlySummary,
          hourlyTotals: historicalAppliedRef.current ? prev.hourlyTotals : live.hourlyTotals,
        }
      })
      if (!historicalAppliedRef.current) {
        setHourlyRows(live.hourlySummary)
        setHourlyTotals(live.hourlyTotals)
      }
    } catch {
      // Silent on soft poll — keep last good frame
    } finally {
      liveInFlightRef.current = false
    }
  }, [])

  useEffect(() => {
    loadLiveOverview()
    loadVolumeInitial()
    return () => {
      if (volumeExpiryPollRef.current != null) {
        window.clearTimeout(volumeExpiryPollRef.current)
      }
    }
  }, [loadLiveOverview, loadVolumeInitial])

  useEffect(() => {
    const id = window.setInterval(softLiveRefresh, LIVE_POLL_MS)
    return () => window.clearInterval(id)
  }, [softLiveRefresh])

  const handleHistoricalApply = async (filters: OpiFilters) => {
    setHistoricalLoading(true)
    try {
      const result = await getOpiHistorical(filters)
      historicalAppliedRef.current = true
      setHourlyRows(result.hourlySummary)
      setHourlyTotals(result.totals)
    } catch (e: any) {
      toaster('error', e?.response?.data?.text || 'Failed to load historical data.')
    } finally {
      setHistoricalLoading(false)
    }
  }

  const handleNotify = useCallback(
    async (
      languageCode: string,
      languageName: string,
      notifySource: 'VolumeExpected' | 'AbandonedLanguage'
    ) => {
      setNotifyingCode(languageCode)
      try {
        const msg = await sendOpiNotify({languageCode, languageName, notifySource})
        toaster('success', msg || 'Notification emails sent successfully.')
        if (notifySource === 'AbandonedLanguage') {
          await loadLiveOverview({soft: true})
        }
      } catch (e: any) {
        toaster('error', e?.response?.data?.text || 'Failed to send notifications.')
      } finally {
        setNotifyingCode(null)
      }
    },
    [loadLiveOverview]
  )

  const handleVolumeNotify = useCallback(
    (code: string, name: string) => handleNotify(code, name, 'VolumeExpected'),
    [handleNotify]
  )

  const onCall = presenceSnapshot?.onCall?.length ?? 0
  const available = presenceSnapshot?.available?.length ?? 0

  const displayScorecards = data?.scorecards
    ? {...data.scorecards, onCall, available, totalOnline: onCall + available}
    : {
        totalOnline: onCall + available,
        onCall,
        available,
        successRate: 0,
        totalCalls: 0,
        connectedCalls: 0,
        abandonedCalls: 0,
        backstopRate: 0,
        backstopSuccessRate: 0,
        transferredTotal: 0,
        effectiffCount: 0,
        bigwordCount: 0,
      }

  const showApiError = error && !data

  return (
    <div className='opi-dashboard'>
      {showApiError ? (
        <div className='opi-empty opi-empty--full'>
          <p>Failed to load dashboard data.</p>
          <button type='button' className='opi-retry-btn' onClick={() => loadLiveOverview()}>
            Try again
          </button>
        </div>
      ) : (
        <OpiScorecards
          scorecards={displayScorecards}
          topAbandonedLanguages={data?.topAbandonedLanguages ?? []}
          presenceReady={!presenceLoading || onCall + available > 0}
          apiReady={!!data}
          onViewInterpreters={() => setShowInterpreterModal(true)}
          onNotifyLanguage={(code, name) => handleNotify(code, name, 'AbandonedLanguage')}
          notifyingCode={notifyingCode}
        />
      )}

      <div className='opi-main-grid'>
        <div className='opi-tables-col'>
          <OpiCallsTable
            title='Calls In Queue'
            calls={callQueues}
            isLoading={presenceLoading}
            durationMode='queue'
          />
          <OpiCallsTable
            title='Live Calls'
            calls={connectedLiveCalls}
            isLoading={presenceLoading}
            durationMode='live'
          />
        </div>

        {volumeLoading && !volume ? (
          <OpiCardSkeleton variant='volume' />
        ) : showApiError ? null : (
          <VolumeExpectedPanel
            items={volume?.volumeExpected ?? []}
            lastUpdated={volume?.lastUpdated ?? ''}
            nextRefreshAt={volume?.nextRefreshAt ?? ''}
            secondsUntilRefresh={volume?.secondsUntilRefresh ?? 0}
            cacheVersion={volume?.lastUpdated ?? ''}
            onNotify={handleVolumeNotify}
            onExpired={loadVolumeOnExpiry}
            notifyingCode={notifyingCode}
          />
        )}
      </div>

      <OpiFilterBar onApply={handleHistoricalApply} isLoading={historicalLoading} />

      {historicalLoading || (apiLoading && !hourlyTotals) ? (
        <OpiCardSkeleton variant='chart' />
      ) : (
        <HourlyCallsChart data={hourlyRows} />
      )}

      {historicalLoading || (apiLoading && !hourlyTotals) ? (
        <OpiCardSkeleton variant='table' />
      ) : hourlyTotals ? (
        <CallsSummaryTable rows={hourlyRows} totals={hourlyTotals} />
      ) : null}

      <InterpreterModal
        show={showInterpreterModal}
        onClose={() => setShowInterpreterModal(false)}
      />
    </div>
  )
}
